import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  AvatarUpload,
  SidePanelCard,
  ToggleRow,
  LinkedCardRow,
} from "../../components/Common/PageWidgets";
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../components/Common/SecondaryButton";
import NotificationPermissionModal from "../../components/Common/NotificationPermissionBanner";
import ConnectAccountModal from "../../components/Common/ConnectAccountModal";
import DeleteConfirmationModal from "../../components/Common/DeleteConfirmationModal";

//Import API
import endpoints from "../../api/endpoints";
import { get, post, postFormData, del, patch } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import editIcon from "../../assets/images/support/edit-icon.svg";
import trashIcon from "../../assets/images/support/trash-icon.svg";
import cardChipIcon from "../../assets/images/support/stripe-account.svg";
import linkIcon from "../../assets/images/support/link-white.svg";
import passwordIcon from "../../assets/images/support/password-icon.svg";
import tickIcon from "../../assets/images/support/tick-circle-white.svg";
import cancelIcon from "../../assets/images/user-dashboard/remove-circle.svg";
import thumbPlaceholder from "../../assets/images/user-dashboard/thumb-placeholder.svg";

/* -------------------------------------------------------------------------
 * SettingsPage — editable profile fields, notification toggles, and
 * linked-account management.
 * ---------------------------------------------------------------------- */

// Validation schema using Yup
const profileSchema = Yup.object().shape({
  first_name: Yup.string()
    .required("First name is required")
    .max(50, "First name must be at most 50 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .max(50, "Last name must be at most 50 characters"),
  mobile: Yup.string()
    .required("Phone number is required")
    .matches(/^\+?[\d\s\-()]+$/, "Invalid phone number format")
    .max(20, "Phone number must be at most 20 characters"),
  zipcode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, "Invalid zip code format (e.g., 68102 or 68102-1234)")
    .max(10, "Zip code must be at most 10 characters"),
  address: Yup.string()
    .max(255, "Address must be at most 255 characters"),
});

const SettingsPage = ({
  profile,
  onProfileFieldChange,
  onEditPhoto,
  onDeletePhoto,
  onChangePassword,
  onSaveChanges,
}) => {
  document.title = "Settings | Omaha Food Lovers";
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    zipcode: "",
    address: "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [deleteAvatar, setDeleteAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [unlinkingAccount, setUnlinkingAccount] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    push_notifications_enabled: true,
    email_notifications_enabled: false,
  });
  const [loadingNotificationSettings, setLoadingNotificationSettings] = useState(false);
  const [updatingNotificationSettings, setUpdatingNotificationSettings] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const fileInputRef = React.useRef(null);

  // Update preview when avatar file changes
  useEffect(() => {
    if (avatarFile) {
      const previewUrl = URL.createObjectURL(avatarFile);
      setAvatarPreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    } else {
      setAvatarPreview(null);
    }
  }, [avatarFile]);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch user profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.me);
      
      if (response.data.success) {
        const data = response.data.data;
        setUserProfile(data);
        setInitialValues({
          first_name: String(data.first_name || ""),
          last_name: String(data.last_name || ""),
          mobile: String(data.mobile || ""),
          zipcode: String(data.address_details?.zipcode || data.address?.zipcode || data.zipcode || ""),
          address: typeof data.address === 'string' ? String(data.address) : String(data.address?.address || ""),
        });
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch linked account data
  const fetchLinkedAccount = useCallback(async () => {
    setLoadingAccount(true);
    try {
      const response = await get(endpoints.financialConnectionsAccounts);
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const account = response.data.data[0];
        setLinkedAccount({
          stripe_account_id: account.stripe_account_id,
          name: account.card_name || account.bank_name || "Linked Account",
          last4: account.last4 || account.account_number?.slice(-4) || "****",
          cashbackPercent: account.cashback_percent || 5,
          processor: account.processor || "Stripe",
        });
      } else {
        setLinkedAccount(null);
      }
    } catch (err) {
      console.error("Failed to fetch linked account:", err);
      setLinkedAccount(null);
    } finally {
      setLoadingAccount(false);
    }
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fetch linked account on mount
  useEffect(() => {
    fetchLinkedAccount();
  }, [fetchLinkedAccount]);

  // Fetch notification settings on mount
  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  // Fetch notification settings from API
  const fetchNotificationSettings = async () => {
    setLoadingNotificationSettings(true);
    try {
      const response = await get(endpoints.notificationSettings);
      if (response.data.success) {
        const data = response.data.data;
        setNotificationSettings({
          push_notifications_enabled: data.push_notifications_enabled ?? true,
          email_notifications_enabled: data.email_notifications_enabled ?? false,
        });
      }
    } catch (err) {
      console.error("Failed to fetch notification settings:", err);
      // Use default values on error
      setNotificationSettings({
        push_notifications_enabled: true,
        email_notifications_enabled: false,
      });
    } finally {
      setLoadingNotificationSettings(false);
    }
  };

  // Update notification settings via API
  const updateNotificationSettings = async (settings, skipSuccessToast = false) => {
    setUpdatingNotificationSettings(true);
    try {
      const response = await patch(endpoints.notificationSettings, settings);
      if (response.data.success) {
        setNotificationSettings(settings);
        if (!skipSuccessToast) {
          toastr.success("Notification settings updated successfully!");
        }
      }
    } catch (err) {
      console.error("Failed to update notification settings:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Revert to previous settings on error
      fetchNotificationSettings();
    } finally {
      setUpdatingNotificationSettings(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setSaving(true);
    try {
      // If avatar file is selected, use FormData
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        formData.append('first_name', values.first_name);
        formData.append('last_name', values.last_name);
        formData.append('mobile', values.mobile);
        formData.append('address[zipcode]', values.zipcode || '');
        formData.append('address[address]', values.address || '');
        
        const response = await postFormData(endpoints.profile, formData);
        
        if (response.data.success) {
          toastr.success("Profile updated successfully!");
          setAvatarFile(null);
          setDeleteAvatar(false);
          navigate("/profile");
        }
      } else if (deleteAvatar) {
        // If avatar should be deleted, include deletion flag
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          mobile: values.mobile,
          address: {
            zipcode: values.zipcode || '',
            address: values.address || '',
          },
          remove_avatar: true,
        };
        
        const response = await post(endpoints.profile, payload);
        
        if (response.data.success) {
          toastr.success("Profile updated successfully!");
          setAvatarFile(null);
          setDeleteAvatar(false);
          navigate("/profile");
        }
      } else {
        // Regular profile update without avatar changes
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          mobile: values.mobile,
          address: {
            zipcode: values.zipcode || '',
            address: values.address || '',
          },
        };
        
        const response = await post(endpoints.profile, payload);
        
        if (response.data.success) {
          toastr.success("Profile updated successfully!");
          setAvatarFile(null);
          setDeleteAvatar(false);
          navigate("/profile");
        }
      }
    } catch (err) {
      console.error("Failed to update profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  // Handle avatar upload
  const handleEditPhoto = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastr.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toastr.error('Image size must be less than 5MB');
      return;
    }

    setAvatarFile(file);
    setDeleteAvatar(false); // Reset delete flag when new file is selected
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle avatar deletion
  const handleDeletePhoto = () => {
    setDeleteAvatar(true);
    setAvatarFile(null);
  };

  // Handle unlink account
  const handleUnlinkAccount = async () => {
    if (!linkedAccount?.stripe_account_id) return;

    setUnlinkingAccount(true);
    try {
      const response = await del(endpoints.financialConnectionsAccount, { account_id: linkedAccount.stripe_account_id });

      if (response.data.success) {
        toastr.success("Account unlinked successfully!");
        setLinkedAccount(null);
        setShowUnlinkDialog(false);
      }
    } catch (err) {
      console.error("Failed to unlink account:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setUnlinkingAccount(false);
    }
  };

  // Handle link account modal
  const handleLinkAccount = () => {
    setShowConnectModal(true);
  };

  // Handle successful account connection
  const handleAccountConnected = () => {
    setShowConnectModal(false);
    fetchLinkedAccount(); // Refresh the linked account data
  };

  // Handle notification toggle
  const handleToggleNotification = async (key, checked) => {
    const newSettings = {
      ...notificationSettings,
      [key]: checked,
    };

    // Handle push notifications permission logic separately
    if (key === "push_notifications_enabled" && checked) {
      // When turning ON, check if we need to request permission
      if ("Notification" in window) {
        const permission = Notification.permission;
        if (permission === "denied") {
          // Permission was previously denied - show modal with browser settings guidance
          setIsPermissionDenied(true);
          setShowPermissionModal(true);
          return; // Don't call API yet, will be called from modal callback
        } else if (permission !== "granted") {
          // Permission is default (not yet asked) - show permission request modal
          setIsPermissionDenied(false);
          setShowPermissionModal(true);
          return; // Don't call API yet, will be called from modal callback
        }
      }
      // If permission is already granted, just update settings
    }

    // Update settings directly for other cases or when permission is already granted
    await updateNotificationSettings(newSettings);
  };

  // Handle password change
  const handlePasswordChange = async () => {
    console.log("handlePasswordChange called");
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      setPasswordSuccess("");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match");
      setPasswordSuccess("");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      setPasswordSuccess("");
      return;
    }

    setChangingPassword(true);
    setPasswordError("");
    setPasswordSuccess("");

    try {
      const response = await post(endpoints.passwordChange, {
        currentPassword,
        password: newPassword,
        confirmPassword,
      });


      if (response.data.status) {
        const successMessage = response.data.message || "Your password has been changed successfully!";
        setPasswordSuccess(successMessage);
        toastr.success(successMessage);
        setShowPasswordForm(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setPasswordSuccess("");
        }, 5000);
      } else {
        setPasswordError(response.data.message || "Failed to change password");
        setPasswordSuccess("");
        toastr.error(response.data.message || "Failed to change password");
      }
    } catch (err) {
      console.error("Failed to change password:", err);
      const errorMessage = extractErrorMessage(err);
      setPasswordError(errorMessage);
      setPasswordSuccess("");
      toastr.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle account deletion - Step 1: Send OTP
  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setDeleteError("");
    try {
      const response = await post(endpoints.deleteAccount);

      if (response.data.success) {
        setDeleteOtpSent(true);
        toastr.success("OTP sent to your email. Please enter the code to confirm deletion.");
      } else {
        toastr.error(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Failed to send delete OTP:", err);
      const errorMessage = extractErrorMessage(err);
      setDeleteError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setDeletingAccount(false);
    }
  };

  // Handle account deletion - Step 2: Confirm with OTP and logout
  const handleDeleteAccountConfirm = async () => {
    if (!deleteOtp || deleteOtp.length < 4) {
      setDeleteError("Please enter the OTP code");
      return;
    }

    setDeletingAccount(true);
    setDeleteError("");
    try {
      const response = await post(endpoints.deleteAccountConfirm, {
        code: deleteOtp,
      });

      if (response.data.success) {
        toastr.success("Account deleted successfully.");

        // Production-level logout: Clear localStorage, sessionStorage, and Redux
        localStorage.clear();
        sessionStorage.clear();

        // Clear Redux store by dispatching logout action
        // Assuming you have a logout action in your Redux store
        // Uncomment and adjust based on your Redux setup:
        // store.dispatch({ type: 'LOGOUT' });
        // or if you have a logout action creator:
        // store.dispatch(logout());

        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        setDeleteError(response.data.message || "Invalid OTP. Please try again.");
        toastr.error(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("Failed to delete account:", err);
      const errorMessage = extractErrorMessage(err);
      setDeleteError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="page-content settings-page">
      {/* Notification Permission Modal */}
      {showPermissionModal && (
        <NotificationPermissionModal
          isDenied={isPermissionDenied}
          onPermissionGranted={() => {
            setShowPermissionModal(false);
            setIsPermissionDenied(false);
            // Re-enable the toggle after permission granted
            const newSettings = {
              ...notificationSettings,
              push_notifications_enabled: true,
            };
            updateNotificationSettings(newSettings);
          }}
          onPermissionDenied={() => {
            setShowPermissionModal(false);
            setIsPermissionDenied(false);
            // Revert the toggle to off without showing success toast
            const newSettings = {
              ...notificationSettings,
              push_notifications_enabled: false,
            };
            updateNotificationSettings(newSettings, true); // Skip success toast
            toastr.warning("Please enable notifications in your browser settings first.");
          }}
        />
      )}

      {/* Connect Account Modal */}
      {showConnectModal && (
        <ConnectAccountModal
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
          onConnect={handleAccountConnected}
        />
      )}

      {/* Unlink Account Confirmation Dialog */}
      <DeleteConfirmationModal
        isOpen={showUnlinkDialog}
        title="Unlink Account"
        message="Are you sure you want to unlink your account? You will stop earning points on purchases at partner restaurants until you link a new account."
        onCancel={() => setShowUnlinkDialog(false)}
        onConfirm={handleUnlinkAccount}
        confirmButtonText="Unlink"
        confirmButtonDisabled={unlinkingAccount}
        confirmButtonLoading={unlinkingAccount}
      />

    <Container fluid>
      <div className="surface-card">
        <div className="profile-header">
          <h2 className="profile-title">Profile Settings</h2>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={profileSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, isSubmitting }) => (
              <Form>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden-file-input"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <div className="profile-layout">
                  <div className="profile-avatar-wrapper">
                    <AvatarUpload
                      key={avatarPreview ? 'preview' : (deleteAvatar ? 'deleted' : 'current')}
                      src={deleteAvatar ? thumbPlaceholder : (avatarPreview || userProfile?.avatar || thumbPlaceholder)}
                      editable
                      editIcon={editIcon}
                      deleteIcon={trashIcon}
                      onEdit={handleEditPhoto}
                      onDelete={handleDeletePhoto}
                    />
                  </div>
                  <div className="fields-grid profile-fields">
                    <div>
                      <CustomInput
                        label="First Name"
                        name="first_name"
                        value={values.first_name}
                        placeholder="e.g. Alex"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.first_name && touched.first_name && <p className="field-error">{errors.first_name}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Last Name"
                        name="last_name"
                        value={values.last_name}
                        placeholder="e.g. Johnson"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.last_name && touched.last_name && <p className="field-error">{errors.last_name}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Email"
                        name="email"
                        value={userProfile?.email || ""}
                        onChange={() => {}}
                        className="white-bg"
                        disabled
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="Phone"
                        name="mobile"
                        value={values.mobile}
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.mobile && touched.mobile && <p className="field-error">{errors.mobile}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Zip Code"
                        name="zipcode"
                        value={values.zipcode}
                        placeholder="e.g. 68102"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.zipcode && touched.zipcode && <p className="field-error">{errors.zipcode}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Address"
                        name="address"
                        value={values.address}
                        placeholder="Enter your address"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.address && touched.address && <p className="field-error">{errors.address}</p>}
                    </div>
                    {showPasswordForm && (
                    <div>
                      <CustomInput
                        label="Current Password"
                        name="currentPassword"
                        type="password"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                    )}
                    {showPasswordForm && (
                    <div>
                      <CustomInput
                        label="New Password"
                        name="newPassword"
                        type="password"
                        placeholder="Enter new password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                    )}
                    {showPasswordForm && (
                    <div>
                      <CustomInput
                        label="Confirm New Password"
                        name="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                    )}
                    {showPasswordForm && passwordError && (
                      <p className="field-error field-error--full-width">{passwordError}</p>
                    )}
                  </div>
                </div>

                {passwordSuccess && !showPasswordForm && (
                  <p className="password-success">{passwordSuccess}</p>
                )}

                <div className="form-actions">
                  <SecondaryButton 
                    label={showPasswordForm ? "Cancel" : "Change Password"} 
                    variant={showPasswordForm ? "light-grey" : "light-orange"} 
                    icon={showPasswordForm ? cancelIcon : passwordIcon} 
                    onClick={() => {
                      setShowPasswordForm(!showPasswordForm);
                      setPasswordError("");
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }} 
                  />
                  <SecondaryButton 
                    label="Delete Account" 
                    variant="light-red" 
                    icon={trashIcon} 
                    onClick={() => setShowDeleteDialog(true)}
                  />
                  {showPasswordForm ? (
                    <SecondaryButton
                      label="Save Password"
                      variant="light-orange"
                      icon={passwordIcon}
                      onClick={handlePasswordChange}
                      disabled={changingPassword}
                      loading={changingPassword}
                    />
                  ) : (
                    <SecondaryButton label="Save Changes" variant="orange" icon={tickIcon} type="submit" disabled={saving || isSubmitting} loading={saving || isSubmitting} />
                  )}
                </div>
              </Form>
            )}
          </Formik>

        {/* Account Deletion Confirmation Dialog */}
        {showDeleteDialog && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999998,
            }}
            onClick={() => setShowDeleteDialog(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "12px",
                maxWidth: "400px",
                width: "90%",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  marginBottom: "12px",
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Delete Account
              </h3>
              {!deleteOtpSent ? (
                <>
                  <p
                    style={{ marginBottom: "24px", color: "#666", lineHeight: "1.5" }}
                  >
                    Are you sure you want to delete your account? This action cannot be undone.
                    All your data including vouchers, points, and account information will be permanently deleted.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <SecondaryButton
                      label="Cancel"
                      variant="light-grey"
                      icon={cancelIcon}
                      onClick={() => setShowDeleteDialog(false)}
                    />
                    <SecondaryButton
                      label="Send OTP"
                      variant="light-red"
                      icon={passwordIcon}
                      onClick={handleDeleteAccount}
                      disabled={deletingAccount}
                      loading={deletingAccount}
                    />
                  </div>
                </>
              ) : (
                <>
                  <p
                    style={{ marginBottom: "16px", color: "#666", lineHeight: "1.5" }}
                  >
                    Enter the OTP code sent to your email to confirm account deletion.
                  </p>
                  <div style={{ marginBottom: "16px" }}>
                    <CustomInput
                      label="OTP Code"
                      name="deleteOtp"
                      type="text"
                      placeholder="Enter OTP"
                      value={deleteOtp}
                      onChange={(e) => setDeleteOtp(e.target.value)}
                      className="white-bg"
                    />
                  </div>
                  {deleteError && (
                    <p style={{ marginBottom: "16px", color: "#ef4444", fontSize: "14px" }}>
                      {deleteError}
                    </p>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <SecondaryButton
                      label="Cancel"
                      variant="light-grey"
                      icon={cancelIcon}
                      onClick={() => {
                        setShowDeleteDialog(false);
                        setDeleteOtpSent(false);
                        setDeleteOtp("");
                        setDeleteError("");
                      }}
                    />
                    <SecondaryButton
                      label="Confirm Delete"
                      variant="light-red"
                      icon={trashIcon}
                      onClick={handleDeleteAccountConfirm}
                      disabled={deletingAccount}
                      loading={deletingAccount}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Row className="g-3 align-items-start">
        <Col xl={6}>
          <SidePanelCard title="Notification Settings">
            {loadingNotificationSettings ? (
              <>
                <div className="toggle-row">
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '150px', height: '20px' }} />
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '46px', height: '26px', borderRadius: '13px' }} />
                </div>
                <div className="toggle-row">
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '150px', height: '20px' }} />
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '46px', height: '26px', borderRadius: '13px' }} />
                </div>
              </>
            ) : (
              <>
                <ToggleRow
                  label="Push Notifications"
                  checked={notificationSettings.push_notifications_enabled}
                  onChange={(checked) => handleToggleNotification("push_notifications_enabled", checked)}
                  disabled={updatingNotificationSettings}
                />
                <ToggleRow
                  label="Email Notifications"
                  checked={notificationSettings.email_notifications_enabled}
                  onChange={(checked) => handleToggleNotification("email_notifications_enabled", checked)}
                  disabled={updatingNotificationSettings}
                />
              </>
            )}
          </SidePanelCard>
        </Col>

        <Col xl={6}>
          <SidePanelCard title="Account Settings">
            {loadingAccount ? (
              <div className="linked-account-card">
                <div className="linked-card-row">
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '44px', height: '44px', borderRadius: '10px' }} />
                  <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '120px', height: '20px' }} />
                </div>
                <div className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '100%', height: '40px', marginTop: '12px' }} />
              </div>
            ) : linkedAccount ? (
              <div className="linked-account-card">
                <LinkedCardRow icon={cardChipIcon} cardName={linkedAccount.name} last4={linkedAccount.last4} />

                <p className="linked-account-card__description">
                  Purchases at partner restaurants with this account automatically earn{" "}
                  {linkedAccount.cashbackPercent}% back in points. Powered securely by{" "}
                  {linkedAccount.processor}.
                </p>
                <div className="unlink-button-wrapper">
                  <SecondaryButton
                    label="Unlink Account"
                    variant="orange"
                    icon={linkIcon}
                    onClick={() => setShowUnlinkDialog(true)}
                    disabled={unlinkingAccount}
                  />
                </div>
              </div>
            ) : (
              <div className="linked-account-card">
                <LinkedCardRow icon={cardChipIcon} cardName="No account linked" last4="----" />
                <p className="linked-account-card__description">
                  Link your account to earn points on every purchase at partner restaurants.
                </p>
                <div className="unlink-button-wrapper">
                  <SecondaryButton
                    label="Link Account"
                    variant="orange"
                    icon={linkIcon}
                    onClick={handleLinkAccount}
                  />
                </div>
              </div>
            )}
          </SidePanelCard>
        </Col>
      </Row>
    </Container>
  </div>
  );
};

SettingsPage.propTypes = {
  profile: PropTypes.shape({
    photoUrl: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string,
    address: PropTypes.string,
  }).isRequired,
  onProfileFieldChange: PropTypes.func,
  onEditPhoto: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onChangePassword: PropTypes.func,
  onSaveChanges: PropTypes.func,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      checked: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onToggleNotification: PropTypes.func,
};

SettingsPage.defaultProps = {
  onProfileFieldChange: () => {},
  onEditPhoto: () => {},
  onDeletePhoto: () => {},
  onChangePassword: () => {},
  onSaveChanges: () => {},
  onToggleNotification: () => {},
  profile: {
    photoUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80",
    firstName: "Alex",
    lastName: "Johnson",
    email: "alex.johnson@example.com",
    phone: "+1 (555) 123-4567",
    zipCode: "68102",
    address: "123 Main Street, Omaha, NE",
  },
  notifications: [
    { key: "email_expiring", label: "Email me when vouchers are expiring", checked: true },
    { key: "push_offers", label: "Push notifications for offers", checked: false },
    { key: "push_points", label: "Push notifications for points", checked: true },
  ],
};

export default SettingsPage;
